/**
 * Three things can claim to know a chat row's last message: the value fetched with the
 * list, the local echo written when this viewer sends, and the live realtime entry. This
 * picks between them.
 */
export type PreviewCandidate = {
  lastMessageAt: string | null;
  preview: string;
} | null | undefined;

export type PickedPreview = { lastMessageAt: string | null; preview: string };

/**
 * The newest candidate, in priority order -- earlier arguments win a tie.
 *
 * Timestamps are compared as dates rather than strings: realtime and PostgREST are not
 * guaranteed to encode timestamptz identically, and the triggers that set last_message_at
 * copy created_at verbatim, so the two are only reliably comparable as instants.
 *
 * A candidate with no timestamp loses to any that has one, which is what keeps a thread
 * with no messages from outranking a real message.
 */
export function pickNewestPreview(...candidates: PreviewCandidate[]): PickedPreview | null {
  let best: PickedPreview | null = null;
  let bestAt = Number.NEGATIVE_INFINITY;

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (candidate.lastMessageAt == null) {
      // No timestamp at all: only worth keeping if nothing else has turned up.
      if (best === null) best = { lastMessageAt: null, preview: candidate.preview };
      continue;
    }
    const at = new Date(candidate.lastMessageAt).getTime();
    if (Number.isNaN(at)) continue;
    if (at > bestAt) {
      bestAt = at;
      best = { lastMessageAt: candidate.lastMessageAt, preview: candidate.preview };
    }
  }

  return best;
}
